import type { Recipe } from '@/types';
import { time } from '@/time';

const layer = (ricottaLine: string) => [
  "Arrange {lasagna:6 noodles} lengthwise, overlapping slightly",
  ricottaLine,
  "Top with {mozzarella cheese:third} slices",
  "Spoon {=1 1/2 cup} meat sauce over mozzarella",
  "Sprinkle with {Parmesan cheese:third}",
];

const recipe: Recipe = {
  title: 'Lasagna',
  source: 'https://www.allrecipes.com/recipe/23600/worlds-best-lasagna/',
  totalTime: time(3, 15),
  servings: 12,
  tags: { type: 'main' },
  vessels: { main: { kind: 'baking-dish', width: 9, height: 13 } },
  wholeOnly: true,
  ingredients: [
    { name: 'sweet Italian sausage', quantity: '1', unit: 'lb' },
    { name: 'lean ground beef', quantity: '3/4', unit: 'lb' },
    { name: 'onion', quantity: '1/2', unit: 'cup',
      note: 'minced',
    },
    { name: 'garlic', quantity: '2', unit: 'cloves', customUnit: true,
      note: 'crushed',
    },
    { name: 'crushed tomatoes', quantity: '28', unit: 'oz' },
    { name: 'tomato sauce', quantity: '13', unit: 'oz' },
    { name: 'tomato paste', quantity: '12', unit: 'oz' },
    { name: 'water', quantity: '1/2', unit: 'cup' },
    { name: 'white sugar', quantity: '2', unit: 'tbsp' },
    { name: 'fresh parsley', quantity: '4', unit: 'tbsp',
      note: 'chopped',
    },
    { name: 'dried basil leaves', quantity: '1 1/2', unit: 'tsp' },
    { name: 'salt', quantity: '1 1/2', unit: 'tsp' },
    { name: 'Italian seasoning', quantity: '1', unit: 'tsp' },
    { name: 'fennel seeds', quantity: '1/2', unit: 'tsp' },
    { name: 'black pepper', quantity: '1/4', unit: 'tsp' },
    { name: 'lasagna', quantity: '12', unit: 'noodles', customUnit: true },
    { name: 'ricotta cheese', quantity: '16', unit: 'oz' },
    { name: 'egg', quantity: '1' },
    { name: 'mozzarella cheese', quantity: '3/4', unit: 'lb',
      note: 'sliced',
    },
    { name: 'Parmesan cheese', quantity: '3/4', unit: 'cup',
      note: 'grated',
    },
  ],
  directions: [
    {
      title: 'Brown meat',
      bullets: [
        "In large pot over medium heat, cook {sweet Italian sausage}, {lean ground beef}, {onion} & {garlic} until well browned",
      ],
    },
    {
      title: 'Simmer sauce',
      bullets: [
        "Stir in {crushed tomatoes}, {tomato sauce}, {tomato paste} & {water}",
        "Season with {white sugar}, {fresh parsley:2 tbsp}, {dried basil leaves}, {salt:1 tsp}, {Italian seasoning}, {fennel seeds} & {black pepper}",
        "Simmer covered ~90 min, stirring occasionally",
      ],
    },
    {
      title: 'Cook noodles',
      bullets: [
        "Bring large pot of lightly salted water to a boil",
        "Cook {lasagna} 8–10 min",
        "Drain noodles & rinse with cold water",
      ],
    },
    {
      title: 'Mix ricotta',
      bullets: [
        "In mixing bowl, combine {ricotta cheese}, {egg}, {fresh parsley:2 tbsp} & {salt:1/2 tsp}",
      ],
    },
    {
      title: 'Assemble first layer',
      perVessel: true,
      bullets: [
        "Spread {=1 1/2 cup} meat sauce in bottom of {vessel}",
        ...layer("Spread with half of ricotta mixture"),
      ],
    },
    {
      title: 'Assemble second layer',
      perVessel: true,
      bullets: layer("Spread with remaining ricotta mixture"),
    },
    {
      title: 'Top & cover',
      perVessel: true,
      bullets: [
        "Top with {mozzarella cheese:third} & {Parmesan cheese:third}",
        "Cover with foil (keep foil off cheese)",
      ],
    },
    {
      title: 'Bake',
      bullets: [
        "Preheat oven to {!375F}",
        "Bake covered 25 min",
        "Remove foil & bake another 25 min",
      ],
    },
    {
      title: 'Rest',
      bullets: [
        "Let rest at room temperature 15 min before cutting",
      ],
    },
  ],
};

export default recipe;
